/* External Dependencies */
import _get from 'lodash/get';
import _forEach from 'lodash/forEach';
import { filter } from 'rxjs/operators';

/* Core Dependencies */
import { AbstractPlugin, MediaPlayerEvents } from 'mlb-media-player';
const HTML5PlayerEvents = MediaPlayerEvents.HTML5;

/* Omniture Dependencies */
import WiretapVideo from 'bam-wiretap/WiretapVideo';
import MLBTokenHelpers from 'bam-wiretap/utils/MLBTokenHelpers';
import PageMeta from 'pageMeta/pageMeta';
import bandwagon from 'bandwagon/bandwagon';
import TokenHelpers from './token-helpers';
const URL = require('bam-url/dist/url');

/**
 *
 * @module OmniturePlugin
 * @desc
 * @class OmniturePlugin
 *
 */

class OmniturePlugin extends AbstractPlugin {

    constructor(config, obsvervationCenter) {

        super(config, obsvervationCenter);

        this.playerSubject = this.observationCenter.playerSubject;

        this.siteTraffic = _get(this.config.omniture, 'siteTraffic', '');

        this.section = _get(this.config.omniture, 'section', '');

        this.wiretapVideoJson = _get(this.config.omniture, 'wiretapVideoJson', {});

        this.bandwagonPlayerEvents = _get(this.config.omniture, 'bandwagonPlayerEvents', {
            MEDIA_LOAD_START: 'loadstart',
            MEDIA_START: 'play',
            MEDIA_COMPLETE: 'ended',
        });

        this.autoPlay = null;

        this.wiretapVideo = null;

        this.dispatcher = null;

    }

    /**
     *
     * @desc Override setMediaPlayer.
     *
     */
    setMediaPlayer(mediaPlayer) {

        /* If we have a mediaPlayer, we need to unsubscribe from the event streams
            because we're going to resubscribe shorlty for the new player */
        if (this.mediaPlayer) {

            this.unsubscribe();

        }

        // Set so init and subscribeToMessages has a reference
        this.mediaPlayer = mediaPlayer;

        this.autoPlay = mediaPlayer.config.playerTypeConfig.autoPlay;

        // init the omniture plugin
        return this.init()
            .then(() => {

                this.subscribeToMessages();

                // Let the super know (this also calls pluginReady())
                super.setMediaPlayer(this.mediaPlayer);

            })
            .catch((error) => {

                console.log('Error when calling setMediaPlayer(this.mediaPlayer)', error);
                return error;

            });

    }

    /**
     *
     * @desc Initialize the plugin.
     *
     */
    init() {

        return new Promise((resolve, reject) => {

            if(this.wiretapVideo) {

                return resolve();

            }

            // BandWagon: register the player events so it knows what to respond to
            bandwagon.registerEvents('PLAYER_EVENTS', HTML5PlayerEvents);

            // Retrieve the dispatcher
            this.dispatcher = bandwagon.getInstanceByKey(`#${_get(this.mediaPlayer, 'player.playerTypeConfig.domTarget', 'media-player')}`);

            //URL Location
            const location = URL.Location();

            // Wiretap tracking variables.
            let wiretapPageConfig = {
                bandwagon: bandwagon,
                siteTraffic: this.siteTraffic,
                property: _get(window, 'videoProperty', ''),
                topicName: _get(window, 'dataBootstrap.title', ''),
                playerContext: location.getParam('playerContext'),
                location: location.getParam('location'),
                systemAutoPlay: this.autoPlay ? 'System Generated' : 'User Generated',
                section: this.section,
                topic: _get(window, 'dataBootstrap', ''),
                langLocale: PageMeta.lang,
            };

            // Team info.
            if (PageMeta.team_id) {

                let allTheTeams = window.team_info;
                let justMyTeam = _.filter(allTheTeams, function(team) {

                    return team.id == PageMeta.team_id;

                });

                wiretapPageConfig['teamAbbrev'] = justMyTeam.abbreviation;
                wiretapPageConfig['teamDisplay'] = justMyTeam.name;

            }

            try {

                const tokenHelpers = Object.assign({}, MLBTokenHelpers, TokenHelpers);

                // Start video tracking (playback).
                this.wiretapVideo = new WiretapVideo(this.wiretapVideoJson, tokenHelpers, wiretapPageConfig);

                resolve();

            } catch(error) {

                reject(error);

            }

        });

    }

    /**
     *
     * @desc Subscribe to the ObservationCenter messages
     *
     */
    subscribeToMessages() {

        // Switch flag to prevent tracking when user pauses and plays the video
        let trackMediaStart = false;

        _forEach(this.bandwagonPlayerEvents, (playerEvent) => {

            let subscription;

            subscription = this.playerSubject.pipe(filter((event) => {

                return event.type === playerEvent;

            })).subscribe((data) => {

                const dataType = data.type;

                // Use media load start to determine when to track 1st time media plays
                const mediaLoadStarted = dataType === this.bandwagonPlayerEvents.MEDIA_LOAD_START;
                const mediaStarted = dataType === this.bandwagonPlayerEvents.MEDIA_START;

                if (mediaLoadStarted) {

                    trackMediaStart = true;

                } else if (trackMediaStart && mediaStarted) {

                    this.dispatchPlayerEvent(dataType);
                    trackMediaStart = false;

                } else if (!mediaLoadStarted && !mediaStarted) {

                    this.dispatchPlayerEvent(dataType);

                }

            });

            this.subscriptions.push(subscription);

        });

    }

    /**
     *
     * @desc Unsubscribe from event streams
     * @private
     *
     */
    unsubscribe() {

        this.subscriptions.forEach((subscription) => {

            subscription.unsubscribe();

        });

    }

    /**
     *
     * @desc Called when the video's PlayerEvent event fires and is found in the bandwagonPlayerEvents object.
     *
     */
    dispatchPlayerEvent(videoPlayerEvent) {

        this.dispatcher.publish(videoPlayerEvent, { player: this.mediaPlayer });

    }

    /**
     *
     * @desc Utility function for returning the name of the class.
     * @return string The class type.
     *
     */
    toString() {

        return 'OmniturePlugin';

    }

}

export default OmniturePlugin;
